import { useState, useEffect, useMemo } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Moon, Sun, Upload, Phone, MessageCircle } from "lucide-react";
import { usePublicData } from "../hooks/usePublicData";
import { usePublicTheme } from "../context/PublicThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { resolveSite } from "../lib/siteConfig";
import SiteLogo from "./SiteLogo";
import { pub } from "@/lib/pubThemeClasses";
import { trackWhatsappClick, trackThemeToggle } from "../lib/tracking";

const NAV_DEF = [
  { to: "/", key: "nav.home", testid: "nav-home" },
  { to: "/hizmetler", key: "nav.services", testid: "nav-services" },
  { to: "/malzemeler", key: "nav.materials", testid: "nav-materials" },
  { to: "/portfoy", key: "nav.portfolio", testid: "nav-portfolio" },
  { to: "/hakkimizda", key: "nav.about", testid: "nav-about" },
  { to: "/iletisim", key: "nav.contact", testid: "nav-contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { settings } = usePublicData();
  const site = resolveSite(settings);
  const { theme, toggleTheme } = usePublicTheme();
  const { t, language, setLanguage } = useLanguage();

  const nav = useMemo(
    () => NAV_DEF.map((n) => ({ ...n, label: t(n.key) })),
    [t],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const bar = scrolled
    ? `bg-white/92 dark:bg-[rgba(10,10,11,0.88)] backdrop-blur-xl border-b ${pub.borderSubtle}`
    : "bg-transparent border-transparent";

  const linkIdle = `text-neutral-700 dark:text-zinc-300 transition-colors ${pub.hoverText}`;

  const langBtn = (lng) =>
    lng === language
      ? `bg-[#FF4F00]/90 text-black font-bold ${pub.hoverText}`
      : `opacity-85 hover:text-[#FF4F00] ${pub.hoverText}`;

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      {/* Topbar — sadece desktop */}
      <div className={`hidden md:flex h-9 items-center justify-end px-10 gap-6 border-b bg-white/95 dark:bg-[#08080a] ${pub.borderSubtle}`}>
        <a
          href={`tel:${site.phone}`}
          data-testid="topbar-phone"
          className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-neutral-500 dark:text-zinc-500 hover:text-[#FF4F00] transition-colors"
        >
          <Phone className="w-3 h-3" strokeWidth={2} />
          {site.phone}
        </a>
        <a
          href={site.whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="topbar-whatsapp"
          onClick={() => trackWhatsappClick("topbar", { language })}
          className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-neutral-500 dark:text-zinc-500 hover:text-[#25D366] transition-colors"
        >
          <MessageCircle className="w-3 h-3" strokeWidth={2} />
          {t("whatsapp.brand")}
        </a>
      </div>

      {/* Ana nav */}
      <div className={`transition-all duration-300 ${bar}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex min-w-0 items-center gap-3 group" data-testid="header-logo">
            <SiteLogo site={site} size="sm" textClassName="truncate text-lg md:text-xl text-neutral-900 dark:text-white" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium tracking-normal transition-colors ${isActive ? "text-[#FF4F00]" : linkIdle}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2 md:gap-3">
            <div className={`hidden sm:flex rounded-sm overflow-hidden border text-[11px] font-bold uppercase tracking-wider ${pub.borderStrongTone}`}>
              <button
                type="button"
                data-testid="header-lang-tr"
                onClick={() => setLanguage("tr")}
                aria-label={t("lang.switchAria.tr")}
                className={`px-2.5 py-2 min-w-[2.5rem] transition-colors ${langBtn("tr")}`}
              >
                {t("lang.tr")}
              </button>
              <button
                type="button"
                data-testid="header-lang-en"
                onClick={() => setLanguage("en")}
                aria-label={t("lang.switchAria.en")}
                className={`px-2.5 py-2 min-w-[2.5rem] border-l transition-colors ${pub.borderSubtle} ${langBtn("en")}`}
              >
                {t("lang.en")}
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                toggleTheme();
                trackThemeToggle(theme === "dark" ? "light" : "dark", { language });
              }}
              aria-label={theme === "dark" ? t("header.themeAriaToLight") : t("header.themeAriaToDark")}
              data-testid="header-theme-toggle"
              className={`hidden sm:inline-flex items-center gap-2 border rounded-sm px-3 py-2 text-xs font-medium tracking-wide bg-white/80 text-neutral-800 transition-colors hover:border-orange-400/70 dark:bg-white/[0.04] dark:text-zinc-200 dark:hover:border-orange-400/50 ${pub.borderStrongTone}`}
            >
              {theme === "dark" ? (
                <>
                  <Sun className="w-4 h-4 text-[#FF4F00]" strokeWidth={2} /> {t("header.theme.light")}
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 text-[#FF4F00]" strokeWidth={2} /> {t("header.theme.dark")}
                </>
              )}
            </button>

            <Link
              to="/teklif-al#teklif-yukle"
              data-testid="header-quote-button"
              className="hidden sm:inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#E64700] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 transition-all hover:-translate-y-0.5"
            >
              <Upload className="w-4 h-4" /> {t("header.uploadModel")} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className={`lg:hidden p-2 border rounded-sm transition-colors hover:border-orange-400/70 dark:hover:border-orange-400/50 ${pub.borderStrongTone}`}
              aria-label={t("header.menuAria")}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5 text-neutral-900 dark:text-white" /> : <Menu className="w-5 h-5 text-neutral-900 dark:text-white" />}
            </button>
          </div>
        </div>

        {open && (
          <div className={`lg:hidden w-full max-w-full border-t bg-white/96 dark:bg-[rgba(10,10,11,0.96)] backdrop-blur-xl ${pub.borderSubtle}`}>
            <nav className="flex flex-col px-6 py-4 gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `py-3 text-base font-medium tracking-normal border-b ${pub.borderSubtle} ${
                      isActive ? "text-[#FF4F00]" : linkIdle
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className={`flex rounded-sm overflow-hidden border my-3 text-[11px] font-bold uppercase tracking-wider ${pub.borderStrongTone}`}>
                <button type="button" data-testid="mobile-lang-tr" onClick={() => setLanguage("tr")} aria-label={t("lang.switchAria.tr")} className={`flex-1 py-3 ${langBtn("tr")}`}>
                  {t("lang.tr")}
                </button>
                <button
                  type="button"
                  data-testid="mobile-lang-en"
                  onClick={() => setLanguage("en")}
                  aria-label={t("lang.switchAria.en")}
                  className={`flex-1 py-3 border-l ${pub.borderSubtle} ${langBtn("en")}`}
                >
                  {t("lang.en")}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  toggleTheme();
                  trackThemeToggle(theme === "dark" ? "light" : "dark", { language });
                  setOpen(false);
                }}
                data-testid="mobile-theme-toggle"
                className={`flex items-center gap-3 py-4 mt-2 text-sm font-medium text-neutral-700 dark:text-zinc-300 border-y ${pub.borderSubtle}`}
              >
                {theme === "dark" ? <Sun className="w-5 h-5 text-[#FF4F00]" /> : <Moon className="w-5 h-5 text-[#FF4F00]" />}
                {theme === "dark" ? t("header.theme.mobile.light") : t("header.theme.mobile.dark")}
              </button>
              <Link
                to="/teklif-al#teklif-yukle"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#FF4F00] text-black font-bold uppercase tracking-wider text-xs px-5 py-3"
                data-testid="mobile-quote-button"
              >
                <Upload className="w-4 h-4" /> {t("header.uploadModel")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </div> {/* /Ana nav wrapper */}
    </header>
  );
}
